import { useEffect, useMemo } from "react";
import {
  DateRange as DateRangeCalendar,
  RangeKeyDict,
  Range,
} from "react-date-range";
import { DateTime } from "luxon";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import {
  useAmenityBookingEngine,
  AmenityDayItem,
  BookingAvailabilityType,
  AmenityDayAvailability,
  CalendarTileStatus,
  BookingError,
} from "./useAmenityBookingEngine";
import { useAvailabilities } from "../DailyBooking";
import { AmenityBookingRequest } from "@/types/Bookings";
import { Button } from "../Button";
import { useMediaQuery } from "@/hooks/useMediaQuery";

export interface DateRange {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
}

type Props = {
  booking: AmenityBookingRequest;
  onUpdateBooking: (booking: AmenityBookingRequest) => void;
  displayAvailabilityInDays: number;
  timezone: string;
  defaultValue?: DateRange; // Date range
  minimumDuration?: number;
  maximumDuration?: number;
  onNext: () => void;
};

function NightlyBooking({
  validDates,
  booking,
  onUpdateBooking,
  displayAvailabilityInDays,
  timezone,
  defaultValue,
  minimumDuration = 1,
  maximumDuration,
  onNext,
}: Props & {
  validDates: string[];
}) {
  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");

  // Convert valid dates to AmenityDayItems
  const initialDayItems = useMemo<AmenityDayItem[]>(() => {
    const validDatesSet = new Set(validDates);
    const items: AmenityDayItem[] = [];

    // Create a date range for the calendar (e.g., next 90 days)
    const today = DateTime.now().setZone(timezone).startOf("day");
    const endDate = today.plus({ days: displayAvailabilityInDays });

    let currentDate = today;
    while (currentDate <= endDate) {
      const key = currentDate.toISODate();
      if (key) {
        const isValid = validDatesSet.has(key);
        const isInDefaultRange =
          defaultValue &&
          key >= defaultValue.startDate &&
          key <= defaultValue.endDate;

        items.push({
          key,
          date: currentDate,
          availability: isValid
            ? AmenityDayAvailability.Available
            : AmenityDayAvailability.Unavailable,
          isSelected: !!isInDefaultRange,
          viewStatus: CalendarTileStatus.Available,
        });
      }
      currentDate = currentDate.plus({ days: 1 });
    }

    return items;
  }, [validDates, timezone, defaultValue]);

  const { dayItems, error, selectedKeys, selectDay, resetSelections } =
    useAmenityBookingEngine({
      initialDayItems,
      availabilityType: BookingAvailabilityType.Range,
      minimumDuration,
      maximumDuration,
      onSelectionChange: (keys) => {
        if (keys.length === 0) {
          onUpdateBooking({ ...booking, startTime: null, endTime: null });
          return;
        }
        const sortedKeys = [...keys].sort();
        const startDate = sortedKeys[0];
        const endDate = sortedKeys[sortedKeys.length - 1];
        const startTime = DateTime.fromISO(startDate, {
          zone: timezone,
        })
          .startOf("day")
          .toUTC();
        const endTime = DateTime.fromISO(endDate, { zone: timezone })
          .startOf("day")
          .toUTC();

        onUpdateBooking({
          ...booking,
          startTime: startTime.toISO(),
          endTime: endDate !== startDate ? endTime.toISO() : null,
        });
      },
    });

  // Alert for min/max duration violations while keeping first selection
  useEffect(() => {
    if (error === BookingError.AboveMaximum && selectedKeys.length === 1) {
      if (maximumDuration) {
        window.alert(
          `Maximum booking duration is ${maximumDuration} day${
            maximumDuration > 1 ? "s" : ""
          }`
        );
      } else {
        window.alert("Maximum booking duration exceeded.");
      }
    }
  }, [error, selectedKeys, maximumDuration]);

  // Convert dayItems to disabled dates for react-date-range
  const disabledDates = useMemo(() => {
    return Object.values(dayItems)
      .filter(
        (item) =>
          item.availability === AmenityDayAvailability.Unavailable ||
          item.viewStatus === CalendarTileStatus.Unavailable
      )
      .map(
        (item) =>
          // Convert to browser's local timezone using date components
          new Date(item.date.year, item.date.month - 1, item.date.day)
      );
  }, [dayItems]);

  // Get the selected date range as JS Dates
  const selectedRange = useMemo<Range>(() => {
    if (selectedKeys.length === 0) {
      return {
        startDate: undefined,
        endDate: undefined,
        key: "selection",
      };
    }

    const sortedKeys = [...selectedKeys].sort();
    const startItem = dayItems[sortedKeys[0]];
    const endItem = dayItems[sortedKeys[sortedKeys.length - 1]];

    // Convert from the specified timezone to browser's local time
    // by creating a date with the same year/month/day in local timezone
    const startDate = startItem
      ? new Date(
          startItem.date.year,
          startItem.date.month - 1,
          startItem.date.day
        )
      : undefined;
    const endDate = endItem
      ? new Date(endItem.date.year, endItem.date.month - 1, endItem.date.day)
      : undefined;

    return {
      startDate,
      endDate,
      key: "selection",
    };
  }, [selectedKeys, dayItems]);

  // Handle date range selection
  const handleSelect = (ranges: RangeKeyDict) => {
    const selection = ranges.selection;
    if (!selection) return;

    const { startDate, endDate } = selection;

    // If only start date is selected
    if (startDate && !endDate) {
      // Convert browser's local date to the specified timezone
      // by using the date components (year, month, day) directly
      const dateTime = DateTime.fromObject(
        {
          year: startDate.getFullYear(),
          month: startDate.getMonth() + 1,
          day: startDate.getDate(),
        },
        { zone: timezone }
      ).startOf("day");
      const key = dateTime.toISODate();
      if (key) {
        selectDay(key);
      }
      return;
    }

    // If both dates are selected
    if (startDate && endDate) {
      const startDateTime = DateTime.fromObject(
        {
          year: startDate.getFullYear(),
          month: startDate.getMonth() + 1,
          day: startDate.getDate(),
        },
        { zone: timezone }
      ).startOf("day");
      const endDateTime = DateTime.fromObject(
        {
          year: endDate.getFullYear(),
          month: endDate.getMonth() + 1,
          day: endDate.getDate(),
        },
        { zone: timezone }
      ).startOf("day");

      const startKey = startDateTime.toISODate();
      const endKey = endDateTime.toISODate();

      if (startKey && endKey) {
        // First select start date
        selectDay(startKey);
        // Then select end date to complete the range
        if (startKey !== endKey) {
          selectDay(endKey);
        }
      }
    }
  };

  // Get min/max dates for the calendar
  const minDate = useMemo(() => {
    const firstAvailable = Object.values(dayItems).find(
      (item) => item.availability === AmenityDayAvailability.Available
    );
    if (firstAvailable) {
      return new Date(
        firstAvailable.date.year,
        firstAvailable.date.month - 1,
        firstAvailable.date.day
      );
    }
    const today = DateTime.now().setZone(timezone).startOf("day");
    return new Date(today.year, today.month - 1, today.day);
  }, [dayItems, timezone]);

  const maxDate = useMemo(() => {
    const sortedItems = Object.values(dayItems).sort(
      (a, b) => b.date.toMillis() - a.date.toMillis()
    );
    const lastAvailable = sortedItems.find(
      (item) => item.availability === AmenityDayAvailability.Available
    );
    if (lastAvailable) {
      return new Date(
        lastAvailable.date.year,
        lastAvailable.date.month - 1,
        lastAvailable.date.day
      );
    }
    const fallback = DateTime.now()
      .setZone(timezone)
      .plus({ days: 90 })
      .startOf("day");
    return new Date(fallback.year, fallback.month - 1, fallback.day);
  }, [dayItems, timezone]);

  return (
    <div className="daily-booking">
      <style>{`
        .rdrDay.rdrDayPassive {
          visibility: hidden;
          pointer-events: none;
        }
        .rdrDay.rdrDayPassive .rdrDayNumber span {
          visibility: hidden;
        }
        .rdrDayDisabled, .rdrDayDisabled .rdrInRange {
          background-color: white !important;
        }
        .rdrDay.rdrDayDisabled .rdrDayNumber span {
          color: #ccc !important;
        }
        .rdrDay.rdrDayDisabled:hover {
          background-color: white !important;
        }
        .rdrDayNumber span {
          color: black;
        }
      `}</style>
      {selectedRange.startDate === undefined &&
        selectedRange.endDate === undefined && (
          <style>{`
       
        span.rdrInRange {
          background-color:white !important;
          color: black;
        } 
          span.rdrInRange ~ span span {
          color:black !important;
          }
      `}</style>
        )}
      <div className="flex justify-center">
        <DateRangeCalendar
          ranges={[selectedRange]}
          onChange={handleSelect}
          minDate={minDate}
          maxDate={maxDate}
          months={isSmallDevice ? 1 : 2}
          direction="horizontal"
          disabledDates={disabledDates}
          showMonthAndYearPickers={false}
          moveRangeOnFirstSelection={false}
          retainEndDateOnFirstSelection={false}
          showDateDisplay={false}
        />
      </div>
      {error !== BookingError.None && (
        <div className="mt-4 text-red-600">
          {error === BookingError.CheckOutOnlyError &&
            "This date is only available for check-out"}
          {error === BookingError.CompositionError &&
            "An error occurred. Please try again."}
        </div>
      )}

      <div className="mt-4 flex justify-between items-center">
        <button
          className="inline-flex items-center p-1 text-sm hover:bg-gray-50"
          onClick={resetSelections}
        >
          Clear Dates
        </button>
        <Button
          color="primary"
          size="small"
          disabled={!booking.startTime || !booking.endTime}
          onClick={onNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function Container(props: Props & { amenitySpaceId: number }) {
  const { amenitySpaceId, ...reset } = props;
  const { data: availabilities = [], isLoading } = useAvailabilities(
    amenitySpaceId,
    new Date()
  );

  const validDates = useMemo(() => {
    return availabilities
      .filter(
        (availability) => availability.availabilityType === 1 // Available
      )
      .map((availability) => availability.startTime.toString().split("T")[0]);
  }, [availabilities]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (validDates.length === 0) {
    return <div>No available dates for booking at this time.</div>;
  }

  return <NightlyBooking validDates={validDates} {...reset} />;
}

export default Container;
